import React, {useRef, useContext} from "react";
import {Dialog, DialogTitle, DialogContent, DialogActions, Slide} from "@mui/material";
import {useDispatch, useSelector} from "../../store";
import Button from "@mui/material/Button";
import {closeUpsertDialog, deleteReview, upsertReview} from "../../slices/reviews-slice";
import {useTranslation} from "next-i18next";
import useWidth from "../../hooks/use-width";
import {TransitionProps} from "@mui/material/transitions";
import IconButton from "@mui/material/IconButton/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import {useAuth} from "../../hooks/use-auth";
import {Formik} from "formik";

import {StyledRating} from "../styled-rating";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DeleteIcon from "@mui/icons-material/Delete";
import {LoadingButton} from "@mui/lab";
import Hidden from "@mui/material/Hidden";
import {scoreProps} from "seacrush-core"
import {TripDataContext} from "../../contexts/trip-context";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction='up' ref={ref} {...props} />;
});


export const ReviewUpsertDialog = () => {

    const {tripPageData} = useContext(TripDataContext);
    const {isAuthenticated, isAdmin} = useAuth()

    const paperRef = useRef<HTMLDivElement>(null);

    const {t} = useTranslation();

    const dispatch = useDispatch()

    const {
        currentUpsertReview,
        isSubmitting,
    } = useSelector(state => {

        const {currentUpsertReview, isSubmitting} = state.reviews
        return {
            currentUpsertReview,
            isSubmitting,
            isLoaded: state.trips.isLoaded,
        }
    });

    const isNewReview = !Boolean(currentUpsertReview?.id)

    const fullScreen = useWidth() === "xs"

    return <Dialog open={!!currentUpsertReview}
                   fullScreen={fullScreen}
                   fullWidth={false}
                   TransitionComponent={Transition}
                   aria-labelledby='new-review-title'
                   sx={{zIndex: 1299}}
                   PaperProps={{ref: paperRef}}
    >

        {isAuthenticated && (

            <Formik
                enableReinitialize={true}
                initialValues={{
                    body: currentUpsertReview?.body,
                    ...currentUpsertReview?.scores
                }}
                validate={(values: any) => {
                    const errors: any = {};
                    if (!values.body) {
                        errors.body = "Required";
                    }

                    return scoreProps.reduce((obj: any, name) => {
                        if (!values[name]) {
                            obj[name] = "required";
                        }
                        return obj;
                    }, errors);
                }}
                onSubmit={async (values, {setSubmitting, resetForm}) => {

                    const scores = scoreProps.reduce((obj: any, name) => {
                        if (values[name]) {
                            // @ts-ignore
                            return {...obj, [name]: parseInt(values[name])};
                        } else {
                            return obj
                        }
                    }, {});

                    dispatch(upsertReview({
                        id: currentUpsertReview?.id || "new",
                        body: values.body || "",
                        scores,
                        tripId: currentUpsertReview?.tripId || "",
                        lang: currentUpsertReview?.lang || "en"
                    }))
                }}
                onReset={(values, {}) => {
                    values.body = "";
                }}
            >
                {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      handleReset,
                      submitCount,
                  }) => (
                    <form onSubmit={handleSubmit}>
                        <DialogTitle
                            id='new-review-title'
                            style={{paddingBottom: 4}}
                        >
                            {t("review-dialog-title", {
                                operatorName: tripPageData.diveOperatorName,
                                locationName: tripPageData.locationName
                            })}
                        </DialogTitle>

                        <DialogContent>
                            {scoreProps.map((name, idx) => {
                                return (
                                    <div key={idx}>
                                        <Box
                                            sx={{
                                                my: 1,
                                                ...(submitCount > 0 && errors[name] && {
                                                  color: "red"
                                                })
                                            }}
                                        >
                                                {t(`score-${name}`)}
                                        </Box>
                                        <StyledRating
                                            name={name}
                                            value={
                                                // @ts-ignore
                                                parseInt(values[name])
                                            }
                                            onChange={handleChange}
                                            disabled={isSubmitting}
                                            size={"medium"}
                                            max={10}
                                            emptyIcon={
                                                <StarBorderIcon
                                                    fontSize='inherit'
                                                    style={{pointerEvents: "none"}}
                                                />
                                            }
                                        />
                                    </div>
                                );
                            })}

                            <TextField
                                variant={"outlined"}
                                disabled={isSubmitting}
                                error={submitCount > 0 && !!errors.body}
                                name='body'
                                multiline
                                minRows={6}
                                fullWidth
                                placeholder={t("add-review-placeholder")}
                                value={values.body}
                                onChange={handleChange}
                                sx={{mt: 3, mb: 2}}
                            />
                        </DialogContent>

                        <DialogActions>
                            <div style={{flexGrow: 1}}>
                                {!isNewReview && currentUpsertReview?.id && (
                                    <IconButton
                                        disabled={isSubmitting}
                                        aria-label='Delete'
                                        // className={classes.button}
                                        onClick={() => {
                                            dispatch(deleteReview(currentUpsertReview?.id || "undef"))
                                            handleReset()
                                        }}
                                    >
                                        <DeleteIcon fontSize='medium'/>
                                    </IconButton>
                                )}
                            </div>
                            <Button
                                disabled={isSubmitting}
                                onClick={e => {
                                    if (!isNewReview) handleReset(e);
                                    dispatch(closeUpsertDialog())
                                }}
                                color='primary'
                            >
                                {t("cancel")}
                            </Button>
                            <LoadingButton
                                type={"submit"}
                                loading={isSubmitting}
                                variant={"contained"}
                                color='primary'
                            >
                                {t("save")}
                            </LoadingButton>
                        </DialogActions>
                        <Hidden mdUp>
                            <Box pb={7} pt={5}/>
                        </Hidden>
                    </form>
                )}
            </Formik>

        )}

        {!isAuthenticated && (
            <>

                <DialogTitle id='new-review-title'>
                    {t("new-review-login-title")}

                    {fullScreen && (
                        <IconButton
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 8,
                                color: (theme) => theme.palette.grey[500],
                            }}
                            color='inherit'
                            disabled={isSubmitting}
                            onClick={e => {
                                dispatch(closeUpsertDialog())
                            }}
                            aria-label='Close'
                        >
                            <CloseIcon/>
                        </IconButton>
                    )}
                </DialogTitle>

                <DialogActions>
                    <Button
                        disabled={isSubmitting}
                        onClick={e => {
                            dispatch(closeUpsertDialog())
                        }}
                        color='primary'
                    >
                        {t("cancel")}
                    </Button>
                </DialogActions>

            </>
        )}


    </Dialog>

}
